/*
 * The portal's own i18next instance, separate from any other in the app.
 * The client's preferred_language picks the language; they can switch it in
 * the header, and the choice sticks on this device.
 */
import { createInstance } from "i18next";
import { useTranslation } from "react-i18next";

import { PORTAL_LANGUAGE_OPTIONS } from "@/features/clientProfile/tabs/personal/personalModel";

import { ar, arPlurals } from "./ar";
import { en } from "./en";
import { es } from "./es";
import { fr } from "./fr";
import { hi } from "./hi";
import { pt } from "./pt";
import { zhHans } from "./zhHans";

export const PORTAL_LANGUAGES = PORTAL_LANGUAGE_OPTIONS.map((o) => ({
  code: o.value,
  /** In its own language: "Español", "中文". */
  name: o.label.split(" · ").pop() ?? o.label
}));

const RTL = new Set(["ar"]);
const STORAGE_KEY = "clientPortalLanguage";

export const isPortalLanguage = (code: string | null | undefined): boolean =>
  Boolean(code) && PORTAL_LANGUAGES.some((l) => l.code === code);

export const languageDirection = (code: string): "rtl" | "ltr" =>
  RTL.has(code) ? "rtl" : "ltr";

/** "zh-CN" → "zh-Hans", "pt-BR" → "pt"; null when we don't have it. */
export const matchPortalLanguage = (
  tag: string | null | undefined
): string | null => {
  if (!tag) return null;
  if (isPortalLanguage(tag)) return tag;
  const base = tag.toLowerCase().split("-")[0];
  if (base === "zh") return /tw|hk|hant/i.test(tag) ? null : "zh-Hans";
  return isPortalLanguage(base) ? base : null;
};

/** The device's choice first, then the client's preferred language, then
 * the browser's. */
export const initialPortalLanguage = (
  preferred: string | null | undefined
): string =>
  matchPortalLanguage(localStorage.getItem(STORAGE_KEY)) ??
  matchPortalLanguage(preferred) ??
  matchPortalLanguage(
    typeof navigator === "undefined" ? null : navigator.language
  ) ??
  "en";

export const rememberPortalLanguage = (code: string): void =>
  localStorage.setItem(STORAGE_KEY, code);

export const portalI18n = createInstance();
void portalI18n.init({
  lng: "en",
  fallbackLng: "en",
  resources: {
    en: { translation: en },
    es: { translation: es },
    pt: { translation: pt },
    "zh-Hans": { translation: zhHans },
    hi: { translation: hi },
    fr: { translation: fr },
    ar: { translation: structuredClone(ar) }
  },
  interpolation: { escapeValue: false },
  initAsync: false
});
portalI18n.addResourceBundle("ar", "translation", arPlurals, true, false);

export const usePortalT = () => useTranslation(undefined, { i18n: portalI18n });
